import { useState, useEffect, useRef } from "react";
import { Plus, Edit, Trash2, Search, X, Upload, Image, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import API_URL from "@/utils/api";

const API = API_URL;
const BASE_URL = API_URL.replace('/api', '');

export default function AdminMenu() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const categoryFileInputRef = useRef(null);

  // Form states
  const [itemForm, setItemForm] = useState({
    name: "",
    description: "",
    price: "",
    menu_price: "",
    category_id: "",
    image: "",
    active: true,
    best_seller: false,
    branch_config: [],
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    image: "",
    order: 0,
  });

  // Image upload handler
  const handleImageUpload = async (file, isCategory = false) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no permitido. Use JPG, PNG, WEBP o GIF");
      return;
    }

    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 5MB");
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      // Build full URL for the image
      const imageUrl = `${BASE_URL}${response.data.url}`;

      if (isCategory) {
        setCategoryForm(prev => ({ ...prev, image: imageUrl }));
      } else {
        setItemForm(prev => ({ ...prev, image: imageUrl }));
      }

      toast.success("Imagen subida correctamente");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al subir la imagen");
    } finally {
      setUploadingImage(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, menuRes, branchRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/public/branches`),
      ]);
      setCategories(catRes.data);
      setMenuItems(menuRes.data);
      setBranches(branchRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredItems = menuItems.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || item.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSaveItem = async () => {
    if (!itemForm.name || !itemForm.price || !itemForm.category_id) {
      toast.error("Completa los campos obligatorios");
      return;
    }

    try {
      const payload = {
        ...itemForm,
        price: parseFloat(itemForm.price),
        menu_price: itemForm.menu_price ? parseFloat(itemForm.menu_price) : null,
      };

      if (editingItem) {
        await axios.put(`${API}/menu/${editingItem.item_id}`, payload, { withCredentials: true });
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/menu`, payload, { withCredentials: true });
        toast.success("Producto creado");
      }

      fetchData();
      setItemDialogOpen(false);
      resetItemForm();
    } catch (error) {
      toast.error("Error al guardar producto");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm("¿Eliminar este producto?")) return;

    try {
      await axios.delete(`${API}/menu/${itemId}`, { withCredentials: true });
      toast.success("Producto eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar producto");
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) {
      toast.error("El nombre es obligatorio");
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.category_id}`, categoryForm, { withCredentials: true });
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/categories`, categoryForm, { withCredentials: true });
        toast.success("Categoría creada");
      }

      fetchData();
      setCategoryDialogOpen(false);
      resetCategoryForm();
    } catch (error) {
      toast.error("Error al guardar categoría");
    }
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      description: item.description || "",
      price: item.price.toString(),
      menu_price: item.menu_price !== undefined && item.menu_price !== null ? item.menu_price.toString() : "",
      category_id: item.category_id,
      image: item.image || "",
      active: item.active,
      best_seller: item.best_seller,
      branch_config: item.branch_config || [],
    });
    setItemDialogOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
      image: category.image || "",
      order: category.order || 0,
    });
    setCategoryDialogOpen(true);
  };

  const resetItemForm = () => {
    setEditingItem(null);
    setItemForm({
      name: "",
      description: "",
      price: "",
      menu_price: "",
      category_id: "",
      image: "",
      active: true,
      best_seller: false,
      branch_config: [],
    });
  };

  // Helper to update specific branch config
  const updateBranchConfig = (branchId, field, value) => {
    setItemForm(prev => {
      const currentConfig = [...(prev.branch_config || [])];
      const existingIndex = currentConfig.findIndex(c => c.branch_id === branchId);

      let newConfigEntry;
      if (existingIndex >= 0) {
        newConfigEntry = { ...currentConfig[existingIndex], [field]: value };
        // If price is empty string, remove it (set to null/undefined)
        if (field === 'price' && value === "") newConfigEntry.price = null;
        if (field === 'price' && value !== "") newConfigEntry.price = parseFloat(value);
        if (field === 'menu_price' && value === "") newConfigEntry.menu_price = null;
        if (field === 'menu_price' && value !== "") newConfigEntry.menu_price = parseFloat(value);

        currentConfig[existingIndex] = newConfigEntry;
      } else {
        // Create new entry
        newConfigEntry = {
          branch_id: branchId,
          active: true, // default
          [field]: field === 'price' ? (value === "" ? null : parseFloat(value)) : value
        };
        currentConfig.push(newConfigEntry);
      }

      return { ...prev, branch_config: currentConfig };
    });
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: "",
      description: "",
      image: "",
      order: 0,
    });
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.category_id === categoryId);
    return cat?.name || "";
  };

  if (isLoading) {
    return (
      <AdminLayout title="Menú">
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Menú">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">Gestión de Menú</h1>
            <p className="text-muted-foreground">{menuItems.length} productos</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={resetCategoryForm} data-testid="add-category-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Categoría
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCategory ? "Editar categoría" : "Nueva categoría"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Nombre *</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      data-testid="category-name-input"
                    />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    />
                  </div>

                  {/* Category Image Upload */}
                  <div className="space-y-3">
                    <Label>Imagen de categoría</Label>

                    {categoryForm.image && (
                      <div className="relative w-full h-32 rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300">
                        <img
                          src={categoryForm.image}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = "https://placehold.co/400x200?text=Error";
                          }}
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-7 w-7"
                          onClick={() => setCategoryForm({ ...categoryForm, image: "" })}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={categoryFileInputRef}
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => handleImageUpload(e.target.files[0], true)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => categoryFileInputRef.current?.click()}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Subiendo...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Subir imagen
                          </>
                        )}
                      </Button>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-muted-foreground">o pegar URL</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <Input
                      placeholder="https://ejemplo.com/imagen.jpg"
                      value={categoryForm.image}
                      onChange={(e) => setCategoryForm({ ...categoryForm, image: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label>Orden</Label>
                    <Input
                      type="number"
                      value={categoryForm.order}
                      onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) })}
                    />
                  </div>
                  <Button className="w-full" onClick={handleSaveCategory} data-testid="save-category-btn">
                    Guardar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetItemForm} data-testid="add-item-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Producto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? "Editar producto" : "Nuevo producto"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Nombre *</Label>
                    <Input
                      value={itemForm.name}
                      onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                      data-testid="item-name-input"
                    />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea
                      value={itemForm.description}
                      onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Precio (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={itemForm.price}
                        onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                        data-testid="item-price-input"
                      />
                    </div>
                    <div>
                      <Label>Precio Menú (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Opcional"
                        value={itemForm.menu_price}
                        onChange={(e) => setItemForm({ ...itemForm, menu_price: e.target.value })}
                        data-testid="item-menu-price-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label>Categoría *</Label>
                      <Select
                        value={itemForm.category_id}
                        onValueChange={(value) => setItemForm({ ...itemForm, category_id: value })}
                      >
                        <SelectTrigger data-testid="item-category-select">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.category_id} value={cat.category_id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Image Upload Section */}
                  <div className="space-y-3">
                    <Label>Imagen del producto</Label>

                    {/* Image Preview */}
                    {itemForm.image && (
                      <div className="relative w-full h-40 rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300">
                        <img
                          src={itemForm.image}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = "https://placehold.co/400x300?text=Error";
                          }}
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-8 w-8"
                          onClick={() => setItemForm({ ...itemForm, image: "" })}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => handleImageUpload(e.target.files[0], false)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Subiendo...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Subir imagen
                          </>
                        )}
                      </Button>
                    </div>

                    {/* URL Input (alternative) */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-muted-foreground">o pegar URL</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <Input
                      placeholder="https://ejemplo.com/imagen.jpg"
                      value={itemForm.image}
                      onChange={(e) => setItemForm({ ...itemForm, image: e.target.value })}
                    />
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={itemForm.active}
                        onCheckedChange={(checked) => setItemForm({ ...itemForm, active: checked })}
                      />
                      <Label>Activo</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={itemForm.best_seller}
                        onCheckedChange={(checked) => setItemForm({ ...itemForm, best_seller: checked })}
                      />
                      <Label>Best seller</Label>
                    </div>
                  </div>
                  {/* Branch Overrides */}
                  {branches.length > 0 && (
                    <div className="border-t pt-4">
                      <Label className="mb-3 block font-semibold text-sm">Precios y Disponibilidad por Sucursal</Label>
                      <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                        {branches.map((branch) => {
                          const config = itemForm.branch_config?.find((c) => c.branch_id === branch.branch_id) || {};
                          const overrideActive = config.active !== false; // Active by default
                          const overridePrice = config.price !== undefined && config.price !== null ? config.price : "";
                          const overrideMenuPrice = config.menu_price !== undefined && config.menu_price !== null ? config.menu_price : "";

                          return (
                            <div key={branch.branch_id} className="flex flex-col gap-3 bg-gray-50 p-3 rounded-lg border border-gray-100">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-gray-700 truncate" title={branch.name}>{branch.name}</span>
                                <div className="flex items-center gap-2" title="Activar/Desactivar en esta sucursal">
                                  <Switch
                                    checked={overrideActive}
                                    onCheckedChange={(checked) => updateBranchConfig(branch.branch_id, "active", checked)}
                                    className="scale-75 data-[state=checked]:bg-green-500"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                <div className="flex items-center gap-2">
                                  <Label className="text-[10px] text-muted-foreground whitespace-nowrap">P. Solo</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    placeholder={itemForm.price || "Base"}
                                    className="h-8 text-xs bg-white"
                                    value={overridePrice}
                                    onChange={(e) => updateBranchConfig(branch.branch_id, "price", e.target.value)}
                                  />
                                  <span className="text-xs text-gray-400">€</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Label className="text-[10px] text-muted-foreground whitespace-nowrap">P. Menú</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    placeholder={itemForm.menu_price || "Base"}
                                    className="h-8 text-xs bg-white"
                                    value={overrideMenuPrice}
                                    onChange={(e) => updateBranchConfig(branch.branch_id, "menu_price", e.target.value)}
                                  />
                                  <span className="text-xs text-gray-400">€</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        * Deja el precio en blanco para usar el precio base. Desactiva el switch para ocultar el producto en esa sucursal.
                      </p>
                    </div>
                  )}
                  <Button className="w-full" onClick={handleSaveItem} data-testid="save-item-btn">
                    Guardar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="menu-search"
            />
          </div>
          <Select value={selectedCategory || "all"} onValueChange={(val) => setSelectedCategory(val === "all" ? "" : val)}>
            <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.category_id} value={cat.category_id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Categories List */}
        <div>
          <h2 className="font-heading font-bold text-lg mb-3">Categorías</h2>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat.category_id}
                variant={selectedCategory === cat.category_id ? "default" : "outline"}
                className="flex items-center gap-2"
                onClick={() => setSelectedCategory(selectedCategory === cat.category_id ? "" : cat.category_id)}
                data-testid={`filter-category-${cat.category_id}`}
              >
                <span className="font-medium">{cat.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditCategory(cat);
                  }}
                  data-testid={`edit-category-${cat.category_id}`}
                >
                  <Edit className="w-3 h-3" />
                </Button>
              </Button>
            ))}
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-sm">Producto</th>
                  <th className="text-left px-4 py-3 font-medium text-sm hidden sm:table-cell">Categoría</th>
                  <th className="text-left px-4 py-3 font-medium text-sm">Precio</th>
                  <th className="text-left px-4 py-3 font-medium text-sm hidden md:table-cell">Estado</th>
                  <th className="text-right px-4 py-3 font-medium text-sm">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.item_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.best_seller && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                              Top
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">
                      {getCategoryName(item.category_id)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-bold">{item.price.toFixed(2)} €</span>
                        {item.menu_price && (
                          <span className="text-xs text-muted-foreground">Menú: {item.menu_price.toFixed(2)} €</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${item.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {item.active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditItem(item)}
                          data-testid={`edit-item-${item.item_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => handleDeleteItem(item.item_id)}
                          data-testid={`delete-item-${item.item_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
